import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { HourglassMedium, CheckCircle, XCircle, Trash } from "@phosphor-icons/react";

/* Awaiting Payment — the money you've earned but haven't been paid yet.
   Record (full or partial) payments as they land; the earned credit stays with
   its relay and nothing funds your banks until cash actually arrives. */
export const PendingPayments = ({ api, fmt, color, today, refreshKey = 0, onSummary }) => {
  const [rows, setRows] = useState([]);
  const [totalAwaiting, setTotalAwaiting] = useState(0);
  const [openId, setOpenId] = useState(null);
  const [rcAmount, setRcAmount] = useState("");
  const [rcDate, setRcDate] = useState(today || "");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${api}/api/caddy/income/pending`, { credentials: "include" });
      if (!res.ok) return;
      const data = await res.json();
      setRows(data.pending || []);
      setTotalAwaiting(data.total_awaiting || 0);
    } catch { /* keep last known */ }
  }, [api]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (!rows.length) return null;

  const applySummary = (data) => { if (data && onSummary) onSummary(data); };

  const recordPayment = async (entry) => {
    const amt = parseFloat(rcAmount);
    if (!amt || amt <= 0 || busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${api}/api/caddy/income/${entry.entry_id}/receipt`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: amt, received_date: rcDate || today }),
      });
      const data = await res.json();
      if (!res.ok) {
        const d = data.detail;
        toast.error(typeof d === "string" ? d : d?.message || "Couldn't record that payment");
        return;
      }
      const rc = data.receipt_recorded;
      const remaining = rc?.entry?.outstanding;
      toast.success(
        rc?.fully_paid
          ? `Paid in full — ${fmt(amt)} landed and your banks can draw from it`
          : `${fmt(amt)} received · ${fmt(remaining != null ? remaining : entry.outstanding - amt)} still awaiting`
      );
      applySummary(data);
      setOpenId(null); setRcAmount("");
      load();
    } catch { toast.error("Network hiccup, try again."); }
    finally { setBusy(false); }
  };

  const correctAmount = async (entry) => {
    if (busy) return;
    const input = window.prompt(`Correct the earned amount for this entry (can't go below the ${fmt(entry.received)} already received):`, String(entry.amount));
    if (input == null) return;
    const val = parseFloat(input);
    if (!val || val <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      const res = await fetch(`${api}/api/caddy/income/${entry.entry_id}`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: val }),
      });
      const data = await res.json();
      if (!res.ok) { const d = data.detail; toast.error(typeof d === "string" ? d : d?.message || "Couldn't correct it"); return; }
      toast.success(`Earned amount corrected to ${fmt(val)}`);
      applySummary(data); load();
    } catch { toast.error("Network hiccup, try again."); }
    finally { setBusy(false); }
  };

  const writeOff = async (entry) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${api}/api/caddy/income/${entry.entry_id}/writeoff`, {
        method: "POST", credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || "Couldn't update that"); return; }
      toast.message("Marked as never paid", { description: "Kept on your relay as earned, not paid. Nothing was taxed or banked." });
      applySummary(data); load();
    } catch { toast.error("Network hiccup, try again."); }
    finally { setBusy(false); }
  };

  const remove = async (entry) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${api}/api/caddy/income/${entry.entry_id}`, {
        method: "DELETE", credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || "Couldn't remove that"); return; }
      toast.success("Pending entry removed");
      applySummary(data); load();
    } catch { toast.error("Network hiccup, try again."); }
    finally { setBusy(false); }
  };

  const reverseReceipt = async (entry, receipt) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${api}/api/caddy/income/${entry.entry_id}/receipt/${receipt.id}`, {
        method: "DELETE", credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || "Couldn't reverse that payment"); return; }
      toast.success(`Reversed ${fmt(receipt.amount)} — it's awaiting payment again`);
      applySummary(data); load();
    } catch { toast.error("Network hiccup, try again."); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl p-5" style={{ background: "var(--pr-card)", border: `1px solid ${color.line}` }} data-testid="pending-payments-card">
      <div className="flex items-center gap-2 mb-1">
        <HourglassMedium size={18} weight="fill" style={{ color: color.gold }} />
        <p className="text-sm font-bold" style={{ color: color.ink }}>Awaiting payment</p>
      </div>
      <p className="text-[11px] mb-4" style={{ color: color.ink2 }} data-testid="pending-total-awaiting">
        {fmt(totalAwaiting)} earned and not yet paid. Record each payment as it lands.
      </p>
      <div className="space-y-3">
        {rows.map((e) => (
          <div key={e.entry_id} className="rounded-xl p-3" style={{ background: "var(--pr-bg)", border: `1px solid ${color.line}` }} data-testid={`pending-row-${e.entry_id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-bold" style={{ color: color.ink }}>
                  {fmt(e.outstanding)} <span className="font-normal" style={{ color: color.muted }}>awaiting</span>
                </p>
                <p className="text-[11px]" style={{ color: color.ink2 }}>
                  Earned {new Date(`${e.date}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  {" · "}{fmt(e.amount)} total
                  {e.received > 0 ? ` · ${fmt(e.received)} received so far` : ""}
                  {e.expected_date ? ` · expected ${new Date(`${e.expected_date}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}
                </p>
              </div>
              <button
                type="button"
                onClick={() => { setOpenId(openId === e.entry_id ? null : e.entry_id); setRcAmount(String(e.outstanding)); setRcDate(today || ""); }}
                className="px-3 py-1.5 rounded-full text-[11px] font-bold flex-shrink-0 pr-btn-primary"
                data-testid={`pending-record-btn-${e.entry_id}`}
              >
                Record payment
              </button>
            </div>

            {(e.receipts && e.receipts.length > 0) && (
              <div className="mt-2 pt-2 space-y-1" style={{ borderTop: `1px dashed ${color.line}` }} data-testid={`pending-receipts-${e.entry_id}`}>
                <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: color.muted }}>Payments received</p>
                {e.receipts.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-2" data-testid={`pending-receipt-${r.id}`}>
                    <p className="text-[11px]" style={{ color: color.ink2 }}>
                      <span className="font-bold" style={{ color: color.ink }}>{fmt(r.amount)}</span>
                      {" · "}{new Date(`${r.received_date}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      {r.assigned_payout ? ` · pays out ${new Date(`${r.assigned_payout}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}
                    </p>
                    <button type="button" onClick={() => reverseReceipt(e, r)} disabled={busy}
                      className="text-[10px] font-semibold inline-flex items-center gap-1 disabled:opacity-40 flex-shrink-0" style={{ color: color.red }}
                      data-testid={`pending-receipt-reverse-${r.id}`}>
                      <XCircle size={12} /> Reverse
                    </button>
                  </div>
                ))}
              </div>
            )}

            {openId === e.entry_id && (
              <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${color.line}` }} data-testid={`pending-record-form-${e.entry_id}`}>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold" style={{ color: color.muted }}>$</span>
                    <input
                      type="number" inputMode="decimal" step="0.01" min="0.01" max={e.outstanding}
                      value={rcAmount}
                      onChange={(ev) => setRcAmount(ev.target.value)}
                      className="w-full pl-7 pr-2 py-2 rounded-lg text-sm font-semibold border"
                      style={{ borderColor: color.line, color: color.ink, background: "var(--pr-card)" }}
                      data-testid={`pending-amount-input-${e.entry_id}`}
                    />
                  </div>
                  <input
                    type="date" value={rcDate} max={today}
                    onChange={(ev) => setRcDate(ev.target.value)}
                    className="px-2 py-2 rounded-lg text-xs border"
                    style={{ borderColor: color.line, color: color.ink, background: "var(--pr-card)" }}
                    data-testid={`pending-date-input-${e.entry_id}`}
                  />
                  <button
                    type="button" disabled={busy || !parseFloat(rcAmount)}
                    onClick={() => recordPayment(e)}
                    className="px-3 py-2 rounded-lg text-xs font-bold disabled:opacity-40 pr-btn-primary inline-flex items-center gap-1"
                    data-testid={`pending-save-btn-${e.entry_id}`}
                  >
                    <CheckCircle size={15} /> Save
                  </button>
                </div>
                <p className="text-[10px] mt-1.5" style={{ color: color.muted }}>
                  Part of it? Enter just what arrived — the rest stays awaiting. No double counting.
                </p>
                <div className="flex items-center gap-3 mt-2">
                  <button type="button" onClick={() => correctAmount(e)} disabled={busy}
                    className="text-[11px] font-semibold inline-flex items-center gap-1 disabled:opacity-40" style={{ color: color.ink2 }}
                    data-testid={`pending-correct-btn-${e.entry_id}`}>
                    Edit amount
                  </button>
                  <button type="button" onClick={() => writeOff(e)} disabled={busy}
                    className="text-[11px] font-semibold inline-flex items-center gap-1 disabled:opacity-40" style={{ color: color.muted }}
                    data-testid={`pending-writeoff-btn-${e.entry_id}`}>
                    <XCircle size={13} /> Never paid (write off)
                  </button>
                  {e.received === 0 && (
                    <button type="button" onClick={() => remove(e)} disabled={busy}
                      className="text-[11px] font-semibold inline-flex items-center gap-1 disabled:opacity-40" style={{ color: color.red }}
                      data-testid={`pending-delete-btn-${e.entry_id}`}>
                      <Trash size={13} /> Remove
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PendingPayments;
